import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { adminApi, authStore } from "../../api/adminApi";
import "../module.scss";

const stamp = value => value ? new Intl.DateTimeFormat("en-SG", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";
const ticketLabel = status => ({ waiting: "Waiting", human: "Assigned", closed: "Closed" }[status] || status || "Unknown");
const localDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const today = localDate(new Date());

const ConversationHistoryPage = () => {
  const site = authStore.user().site?.key || "cloudkia";
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [preset, setPreset] = useState("today");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [appliedFilter, setAppliedFilter] = useState({ preset: "today" });
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  const loadConversation = useCallback(async id => {
    setDetailLoading(true);
    setError("");
    try { setSelected(await adminApi.conversation(site, id)); }
    catch (loadError) { setError(loadError.message); }
    finally { setDetailLoading(false); }
  }, [site]);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await adminApi.conversations(site, appliedFilter);
      const rows = result.data || [];
      setItems(rows);
      const wanted = params.get("id");
      if (wanted) await loadConversation(wanted);
    } catch (loadError) { setError(loadError.message); }
    finally { setLoading(false); }
  }, [site, params, loadConversation, appliedFilter]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const filtered = useMemo(() => items.filter(item => `${item.session?.dealer_name || ""} ${item.session?.last_path || ""} ${item.id}`.toLowerCase().includes(query.toLowerCase())), [items, query]);
  const openConversation = async id => {
    setParams({ id });
    await loadConversation(id);
  };
  const changePreset = event => {
    const value = event.target.value;
    setPreset(value);
    if (value !== "custom") setAppliedFilter({ preset: value });
  };
  const applyCustomRange = () => {
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const days = Math.floor((end - start) / 86400000) + 1;
    if (!startDate || !endDate || Number.isNaN(days) || days < 1) return setError("Choose a valid start and end date.");
    if (days > 60) return setError("Custom date range cannot exceed 60 days.");
    setError("");
    setAppliedFilter({ preset: "custom", startDate, endDate });
  };

  return <div className="module-page">
    <div className="module-page__intro conversation-history-intro"><div><h2>Conversations</h2><p>Persistent visitor and assistant message history. Support work is managed in Tickets.</p></div><div className="conversation-history-filters"><label>Period<select value={preset} onChange={changePreset}><option value="unread">Unread</option><option value="today">Today</option><option value="yesterday">Yesterday</option><option value="this_week">This week</option><option value="last_week">Last week</option><option value="this_month">This month</option><option value="custom">Custom date</option></select></label>{preset === "custom" && <><label>From<input type="date" value={startDate} max={endDate || today} onChange={event => setStartDate(event.target.value)} /></label><label>To<input type="date" value={endDate} min={startDate} max={today} onChange={event => setEndDate(event.target.value)} /></label><button className="secondary-button" type="button" onClick={applyCustomRange}>Apply</button></>}<button className="secondary-button" type="button" onClick={loadConversations} disabled={loading}>Refresh</button></div></div>
    {error && <div className="notice notice--error" role="alert">{error}</div>}
    <div className="conversation-layout">
      <section className="conversation-list">
        <div className="queue-heading"><strong>Visitor threads <span>{items.length}</span></strong><small>Conversation history across visitor sessions</small></div>
        <div className="search-box"><span>⌕</span><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search conversations" aria-label="Search conversations" /></div>
        {loading ? <div className="page-loading"><p>Loading conversations…</p></div> : filtered.length ? filtered.map(item => <button key={item.id} className={`conversation-row ${selected?.id === item.id ? "active" : ""}`} onClick={() => openConversation(item.id)}>
          <span className="conversation-row__avatar">{(item.session?.dealer_name || "V")[0]}</span>
          <span className="conversation-row__body"><strong>{item.session?.dealer_name || "Website visitor"}</strong><small>{item.session?.last_path || item.session?.landing_path || "Traffidio website"}</small><em>{item.messages_count || 0} messages · {item.tickets_count || 0} tickets</em></span>
          <span className="conversation-row__time">{stamp(item.last_message_at)}</span>
        </button>) : <div className="page-empty"><p>No conversations found.</p></div>}
      </section>
      <section className="conversation-detail">
        {selected ? <>
          <header><div><h3>{selected.session?.dealer_name || "Website visitor"}</h3><p>Conversation {String(selected.id).slice(0, 12)} · {selected.grouped_conversation_count || 1} thread{selected.grouped_conversation_count === 1 ? "" : "s"}</p></div><span className="pill pill--open">Ongoing</span></header>
          <div className="context-strip"><span>Page <strong>{selected.session?.last_path || selected.session?.landing_path || "/"}</strong></span><span>Started <strong>{stamp(selected.session?.started_at || selected.created_at)}</strong></span><span>Last seen <strong>{stamp(selected.session?.last_seen_at)}</strong></span></div>
          {!!selected.tickets?.length && <div className="conversation-actions"><strong>Linked tickets</strong>{selected.tickets.map(ticket => <Link className={`pill pill--${ticket.status}`} key={ticket.id} to={`/tickets?id=${encodeURIComponent(ticket.id)}`}>Ticket {String(ticket.id).slice(0, 8)} · {ticketLabel(ticket.status)}</Link>)}</div>}
          <div className="message-stream">{detailLoading ? <div className="page-loading"><p>Loading messages…</p></div> : selected.messages?.length ? selected.messages.map(message => <div className={`message message--${message.sender_type}`} key={message.id}>
            <small>{message.sender_type === "visitor" ? "Visitor" : message.sender_type === "agent" ? (message.sender?.name || "Agent") : "Chatbot"}</small>
            <p>{message.content || "Attachment"}</p>
            <time>{stamp(message.created_at)}</time>
          </div>) : <div className="page-empty"><p>No messages in this conversation.</p></div>}</div>
        </> : <div className="conversation-placeholder"><span>◎</span><h3>Choose a conversation</h3><p>The visitor’s persistent message history will appear here.</p></div>}
      </section>
    </div>
  </div>;
};

export default ConversationHistoryPage;
